import React, { useState } from 'react'
import { Button, Col, Flex, Form, notification, Typography, UploadFile } from 'antd'
import { useImportVendorCostReportMutation } from '@/features/employee-data/employee-data.api.ts'
import { AntdForm } from '@/common/components/AntdForm'
import { AntdFormRules } from '@/common/components/AntdForm/rules.tsx'
import Title from 'antd/es/typography/Title'
import { handleApiError } from '@/utils/api.tsx'
import { Country, CountryOptions } from '@/common/types/work-info.def.ts'
import { VendorCostReportType, VendorCostReportTypeOptions } from '@/common/types/staff-cost.ts'


export const VendorCostReportImportForm: React.FC = () => {
  const [importPayrollReport, { isLoading }] = useImportVendorCostReportMutation()
  const [sheetNames, setSheetNames] = useState<string[]>([])

  const [form] = Form.useForm()
  const watch = Form.useWatch([], form) as { type: string, country: string }
  const noNeedSheetName = watch?.country === Country.Japan && watch?.type === VendorCostReportType.Payroll

  const onImport = async ({ type, country, excelFiles, sheetName }: {
    type: string,
    country: string,
    excelFiles: UploadFile[],
    sheetName: string
  }) => {
    const excelFile = excelFiles[0]
    const result = await importPayrollReport({
      type,
      country,
      sheetName: noNeedSheetName ? 'noNeeded' : sheetName,
      excelFile: excelFile.originFileObj as unknown as File,
    })
    if ('data' in result) {
      notification.success({ message: 'Import Successfully' })
    } else {
      handleApiError(result.error)
    }
  }

  return (
    <Flex vertical gap="small">
      <Title level={4}>Vendor Cost Report Import</Title>
      <Typography.Text>Please select the country and upload the specified report.</Typography.Text>
      <Typography.Text>You can only import reports of your managed countries.</Typography.Text>
      <Form layout="vertical" form={form} onFinish={onImport}>
        <AntdForm.Section>
          <AntdForm.Select
            fieldProps={{ name: 'type', rules: [AntdFormRules.required], span: 8 }}
            options={VendorCostReportTypeOptions}
          />
          <AntdForm.Select
            fieldProps={{ name: 'country', rules: [AntdFormRules.required], span: 8 }}
            options={CountryOptions}
          />
          <Col span={8} />
          <AntdForm.ExcelInput
            fieldProps={{ name: 'excelFiles', label: 'Excel File', rules: [AntdFormRules.required], span: 8 }}
            onWorkSheetsListChange={val => {
              form.resetFields(['sheetName'])
              setSheetNames(val)
              if (val.length === 1) {
                form.setFieldValue('sheetName', val[0])
              }
            }}
          />
          {noNeedSheetName
            ? <Typography.Text>Sheet name is not needed for {Country.Japan} {VendorCostReportType.Payroll}</Typography.Text>
            : <AntdForm.Select
              fieldProps={{ name: 'sheetName', rules: [AntdFormRules.requiredIf(noNeedSheetName)], span: 8 }}
              disabled={sheetNames.length === 0}
              options={sheetNames.map(x => ({ label: x, value: x }))}
            />
          }
        </AntdForm.Section>
        <Flex gap="small">
          <Button htmlType="submit" type="primary" loading={isLoading}>
            Import
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}
